import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

/**
 * Cloudflare Turnstile widget — explicit rendering.
 *
 * Explicit rendering retains the widget ID so the challenge can be reset: a
 * `cf-turnstile-response` token is single-use, so the widget must be reset
 * after a failed submission before the user can retry.
 *
 * The token is redeemed server-side by the API Worker (siteverify). It is never
 * verified from the browser.
 *
 * ── Why connector-service needs this at all ────────────────────────────────
 * `POST /api/v1/auth/login` gates every non-device client on Turnstile:
 *
 *     const isDeviceClient = clientType === 'pos' || clientType === 'mobile'
 *     const skipsCaptcha = isDeviceClient && clientDeviceId.length > 0
 *
 * The connector sends `x-client-type: connector-service` and a device id, but it
 * is not in that device list, so the captcha is REQUIRED. Without a widget the
 * login could never succeed — it returned
 * `403 { code: 'TURNSTILE_FAILED', error: 'Verification challenge missing' }`
 * on every attempt. This file is the tenant-admin implementation, copied rather
 * than re-invented, because both apps authenticate against the same endpoint and
 * must produce identical tokens.
 */

export const TURNSTILE_SITE_KEY = '0x4AAAAAAE_K1-UtiUsaL4yi'

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

export interface TurnstileHandle {
  /** Current token, or null if the challenge has not completed. */
  getToken: () => string | null
  /** Clears the widget so a fresh token can be issued for a retry. */
  reset: () => void
}

interface TurnstileApi {
  render: (element: HTMLElement, options: Record<string, unknown>) => string
  reset: (widgetId?: string) => void
  remove: (widgetId: string) => void
}

type TurnstileWindow = Window & { turnstile?: TurnstileApi }

let scriptPromise: Promise<void> | null = null

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()

  const w = window as TurnstileWindow
  if (w.turnstile) return Promise.resolve()
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise<void>((resolve, reject) => {
    let settled = false
    const finish = (ok: boolean) => {
      if (settled) return
      settled = true
      window.clearInterval(poll)
      ok ? resolve() : reject(new Error('Turnstile failed to load'))
    }

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`)
    if (!existing) {
      const script = document.createElement('script')
      script.src = SCRIPT_SRC
      script.async = true
      script.defer = true
      script.addEventListener('load', () => finish(true))
      script.addEventListener('error', () => finish(false))
      document.head.appendChild(script)
    }

    const startedAt = Date.now()
    const poll = window.setInterval(() => {
      if ((window as TurnstileWindow).turnstile) finish(true)
      else if (Date.now() - startedAt > 10_000) finish(false)
    }, 100)
  })

  return scriptPromise
}

interface TurnstileWidgetProps {
  /** Action name, e.g. `staff_login`. Must match the action the backend expects. */
  action: string
  className?: string
}

export const TurnstileWidget = forwardRef<TurnstileHandle, TurnstileWidgetProps>(
  function TurnstileWidget({ action, className }, ref) {
    const containerRef = useRef<HTMLDivElement>(null)
    const widgetIdRef = useRef<string | null>(null)
    const tokenRef = useRef<string | null>(null)

    useImperativeHandle(
      ref,
      () => ({
        getToken: () => tokenRef.current,
        reset: () => {
          tokenRef.current = null
          const w = window as TurnstileWindow
          if (w.turnstile && widgetIdRef.current) {
            try {
              w.turnstile.reset(widgetIdRef.current)
            } catch {
              /* widget already gone */
            }
          }
        },
      }),
      []
    )

    useEffect(() => {
      let cancelled = false

      loadTurnstileScript()
        .then(() => {
          const w = window as TurnstileWindow
          if (cancelled || !w.turnstile || !containerRef.current) return
          if (widgetIdRef.current) return

          widgetIdRef.current = w.turnstile.render(containerRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            action,
            theme: 'auto',
            callback: (token: string) => {
              tokenRef.current = token
            },
            'expired-callback': () => {
              tokenRef.current = null
            },
            'error-callback': () => {
              tokenRef.current = null
            },
          })
        })
        .catch(() => {
          // Leave the container empty; the submit handler reports the failure and
          // the server rejects the request regardless.
        })

      return () => {
        cancelled = true
        const w = window as TurnstileWindow
        if (w.turnstile && widgetIdRef.current) {
          try {
            w.turnstile.remove(widgetIdRef.current)
          } catch {
            /* already removed */
          }
          widgetIdRef.current = null
        }
      }
    }, [action])

    return <div ref={containerRef} className={className} data-action={action} />
  }
)
